'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@morpho-org/ui-v6/utils';
import { Button } from '@repo/ui';
import { Popover as RadixPopover } from 'radix-ui';
import { type ForwardedRef, forwardRef, type ReactNode, use } from 'react';
import { MasterFilterContext } from './MasterFilter';
const masterFilterContentStyles = cn([
// Base styles
'flex flex-col', 'bg-popover overflow-hidden rounded-lg', 'border-[0.5px] border-border', 'shadow-lg z-60',
// Animations
'data-[state=open]:data-[side=top]:animate-slide-up-and-fade', 'data-[state=open]:data-[side=right]:animate-slide-left-and-fade', 'data-[state=open]:data-[side=bottom]:animate-slide-down-and-fade', 'data-[state=open]:data-[side=left]:animate-slide-right-and-fade',
// Responsive — must flip at the same width as MasterFilterAdvancedSelect's
// useIsBelowBreakpoint(768) mobile mode
'max-md:w-[var(--radix-popover-content-available-width)]']);
export interface MasterFilterContentProps {
  children: ReactNode;
  width?: string;
  side?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
  className?: string;
}
function MasterFilterContentInner(t0, ref) {
  const $ = _c(21);
  const {
    children,
    width: t1,
    side: t2,
    align: t3,
    sideOffset: t4,
    className
  } = t0;
  const width = t1 === undefined ? "340px" : t1;
  const side = t2 === undefined ? "bottom" : t2;
  const align = t3 === undefined ? "start" : t3;
  const sideOffset = t4 === undefined ? 8 : t4;
  const context = use(MasterFilterContext);
  if (!context) {
    throw new Error("MasterFilter.Content must be used within a MasterFilter.Provider");
  }
  let t5;
  if ($[0] !== context.meta || $[1] !== ref) {
    t5 = node => {
      if (typeof ref === "function") {
        ref(node);
      } else {
        if (ref) {
          ref.current = node;
        }
      }
      context.meta.setContentElement(node);
    };
    $[0] = context.meta;
    $[1] = ref;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const mergedRef = t5;
  let t6;
  if ($[3] !== width) {
    t6 = {
      width,
      maxHeight: "var(--radix-popover-content-available-height)"
    };
    $[3] = width;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== className) {
    t7 = cn(masterFilterContentStyles, className);
    $[5] = className;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== children) {
    t8 = <div className="gap-xs flex flex-col overflow-y-auto p-[16px]">{children}</div>;
    $[7] = children;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const t9 = context.meta.activeCount === 0;
  let t10;
  if ($[9] !== context.actions.clearAll || $[10] !== t9) {
    t10 = <div className="border-border py-xs border-t-[0.5px] px-[16px]"><Button variant="destructive" size="sm" disabled={t9} onClick={context.actions.clearAll} data-testid="master-filter-reset">Reset</Button></div>;
    $[9] = context.actions.clearAll;
    $[10] = t9;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== align || $[13] !== mergedRef || $[14] !== side || $[15] !== sideOffset || $[16] !== t10 || $[17] !== t6 || $[18] !== t7 || $[19] !== t8) {
    t11 = <RadixPopover.Portal><RadixPopover.Content ref={mergedRef} side={side} align={align} sideOffset={sideOffset} collisionPadding={15} style={t6} className={t7}>{t8}{t10}</RadixPopover.Content></RadixPopover.Portal>;
    $[12] = align;
    $[13] = mergedRef;
    $[14] = side;
    $[15] = sideOffset;
    $[16] = t10;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  return t11;
}
export const MasterFilterContent = forwardRef(MasterFilterContentInner);
MasterFilterContent.displayName = 'MasterFilterContent';